import React, { useEffect, useRef, useState } from "react";
import { Search, PackagePlus } from "lucide-react";
import { api } from "../lib/api";
import { money, bn } from "../lib/format";

export function useDebounced<T>(v: T, ms = 250) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

export default function ProductSearch({ onPick, onQuickAdd, placeholder = "পণ্যের নাম / কোড দিয়ে খুঁজুন...", testid = "memo-product-search-input" }: any) {
  const [q, setQ] = useState("");
  const [list, setList] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const dq = useDebounced(q);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dq.trim()) {
      setList([]);
      return;
    }
    api.get("/products", { params: { q: dq } }).then((r) => { setList(r.data.slice(0, 8)); setHi(0); }).catch(() => null);
  }, [dq]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (p: any) => {
    onPick(p);
    setQ("");
    setList([]);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (list[hi]) pick(list[hi]); }
  };

  return (
    <div className="relative" ref={box}>
      <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input className="inp pl-10" value={q} placeholder={placeholder} data-testid={testid}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey} />
      {open && q.trim() && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden" data-testid="product-search-results">
          {list.map((p, i) => (
            <button type="button" key={p.id} onMouseDown={(e) => { e.preventDefault(); pick(p); }} onMouseEnter={() => setHi(i)}
              className={`w-full text-left px-4 py-2.5 flex justify-between gap-3 ${i === hi ? "bg-blue-50" : ""}`} data-testid={`product-search-result-${i}`}>
              <span>
                <span className="font-semibold text-slate-800">{p.name}</span>
                <span className="block text-xs text-slate-500">{[p.company, p.category].filter(Boolean).join(" · ")}</span>
              </span>
              <span className="text-right shrink-0">
                <span className="num font-semibold text-blue-700">৳ {money(p.rate)}</span>
                <span className={`block text-xs ${p.stock <= p.low_stock ? "text-red-600" : "text-slate-500"}`}>স্টক: {bn(p.stock)} {p.unit}</span>
              </span>
            </button>
          ))}
          {list.length === 0 && <p className="px-4 py-3 text-sm text-slate-500">"{q}" নামে কোনো পণ্য পাওয়া যায়নি</p>}
          {onQuickAdd && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); onQuickAdd(q); setOpen(false); setQ(""); }}
              className="w-full text-left px-4 py-2.5 border-t border-slate-100 text-emerald-700 font-semibold flex items-center gap-2 hover:bg-emerald-50" data-testid="product-search-quick-add">
              <PackagePlus size={17} /> "{q}" নতুন পণ্য হিসেবে দ্রুত যোগ করুন
            </button>
          )}
        </div>
      )}
    </div>
  );
}
